import { Trans } from '@lingui/react';
import clsx from 'clsx';
import { useKeenSlider } from 'keen-slider/react';
import type { ImageProps } from 'next/image';
import Image from 'next/image';
import { useState } from 'react';
import { Badge, BadgeType } from '../../../components/ui/badge/Badge';

import 'keen-slider/keen-slider.min.css';

import { Button } from '../../../components/ui/button/Button';
import ArrowLeft from '../../../icons/direction/arrow/left.svg';
import ArrowRight from '../../../icons/direction/arrow/right.svg';

import styles from './BlogHero.module.scss';

export type BlogHeroResponsiveImageType = {
  mobile: ImageProps;
  desktop?: ImageProps;
};

export type BlogHeroSlideProps = {
  images?: Array<BlogHeroResponsiveImageType>;
  featuredImage?: BlogHeroResponsiveImageType;
  title?: string;
  description?: string;
  subtitle?: string;
  buttonLabel?: string;
  buttonLink?: string;
  newTab?: boolean;
  proOnly?: boolean;
};

export type BlogHeroProps = {
  slides?: Array<BlogHeroSlideProps>;
};

/**
 * A basic responsive image element.
 * @param {Object} props - The component props.
 * @returns {ReactNode}
 */
const ResponsiveImage = ({
  mobile,
  desktop,
  featuredImage = false,
}: {
  mobile: ImageProps;
  desktop?: ImageProps;
  featuredImage?: boolean;
}) => (
  <div className={styles.blogHero__image}>
    <Image
      className={styles['blogHero__image--desktop']}
      sizes="33vw"
      fill
      src={desktop?.src || mobile.src}
      alt={desktop?.alt || mobile.alt}
      title={desktop?.alt || mobile.alt}
    />
    <Image
      className={styles['blogHero__image--mobile']}
      sizes={featuredImage ? '100vw' : '50vw'}
      fill
      src={mobile.src}
      alt={mobile.alt}
      title={mobile.alt}
    />
  </div>
);

/**
 * The Blog Hero section component.
 * @param {Object} props - The component props.
 * @returns {ReactNode}
 */
export const BlogHero = ({ slides }: BlogHeroProps) => {
  const [current, setCurrent] = useState(0);

  const [sliderElement, slider] = useKeenSlider({
    /**
     * Handles the slide changed event.
     * @param {Object} slider - The slider instance.
     */
    slideChanged(slider) {
      setCurrent(slider.track.details.rel);
    },
  });

  return (
    <section className={styles.blogHero}>
      {slides && slides.length >= 2 && (
        <button
          className={styles.blogHero__button}
          type={'button'}
          onClick={() => slider.current?.prev()}
          disabled={current === 0}
        >
          <span className={'visually-hidden'}>
            <Trans
              id={'blogHero.previousSlide'}
              message={'Go to previous slide'}
            />
          </span>

          <ArrowLeft />
        </button>
      )}

      <div ref={sliderElement} className={'keen-slider'}>
        {slides &&
          slides.map((slide, index) => (
            <div
              key={index}
              className={clsx('keen-slider__slide', styles.blogHero__slide)}
            >
              <div className={styles.blogHero__grid}>
                {slide.images &&
                  (slide.images[0].desktop || slide.images[0].mobile) && (
                    <ResponsiveImage {...slide.images[0]} />
                  )}

                {slide.images &&
                  (slide.images[1].desktop || slide.images[1].mobile) && (
                    <ResponsiveImage {...slide.images[1]} />
                  )}

                <div className={styles.blogHero__content}>
                  {(slide.featuredImage?.desktop ||
                    slide.featuredImage?.mobile) && (
                    <ResponsiveImage
                      {...slide.featuredImage}
                      featuredImage={true}
                    />
                  )}

                  <div className={styles.blogHero__copy}>
                    {slide.proOnly && (
                      <Badge
                        badge={{ type: BadgeType.proOnly }}
                        className={styles.blogHero__proBadge}
                      />
                    )}

                    {slide.subtitle && (
                      <h2
                        className={clsx(
                          'text-utility-utility-small text-utility-utility-desktop',
                          styles.blogHero__subtitle
                        )}
                      >
                        {slide.subtitle}
                      </h2>
                    )}

                    {slide.title && (
                      <h1
                        className={clsx(
                          'text-blog-heading-xl-mobile text-blog-heading-xl-desktop',
                          styles.blogHero__title
                        )}
                      >
                        {slide.title}
                      </h1>
                    )}

                    {slide.description && (
                      <p
                        className={clsx(
                          'text-blog-body-mobile text-blog-body-desktop',
                          styles.blogHero__body
                        )}
                      >
                        {slide.description}
                      </p>
                    )}

                    {slide.buttonLabel && slide.buttonLink && (
                      <Button
                        modifiers="text"
                        href={slide.buttonLink}
                        iconPosition={'right'}
                        className={styles.blogHero__action}
                        target={slide.newTab ? '_blank' : undefined}
                      >
                        {slide.buttonLabel}
                        <ArrowRight />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>

      {slides && slides.length >= 2 && (
        <button
          className={clsx(
            styles.blogHero__button,
            styles['blogHero__button--next']
          )}
          type={'button'}
          onClick={() => slider.current?.next()}
          disabled={slides && current === slides.length - 1}
        >
          <span className={'visually-hidden'}>
            <Trans id={'blogHero.nextSlide'} message={'Go to next slide'} />
          </span>

          <ArrowRight />
        </button>
      )}

      {slides && slides.length >= 2 && (
        <ul className={styles.blogHero__pagination}>
          {slides.map((_, index) => (
            <li key={index}>
              <button
                className={clsx(styles.blogHero__dot, {
                  [styles['blogHero__dot--is-active']]: current === index,
                })}
                type={'button'}
                onClick={() => slider.current?.moveToIdx(index)}
              >
                <span className={'visually-hidden'}>
                  <Trans
                    id={'blogHero.goToSlide'}
                    message={'Go to slide {number}'}
                    values={{
                      number: index + 1,
                    }}
                  />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
